import { consola } from 'consola'
import { ref, computed, onUnmounted } from 'vue'
import type { Order, OrderSummary } from '@entities/order'
import { PAYMENT_STATUSES } from '@shared/config'

/**
 * Константы для polling
 */
const POLLING_INTERVAL_MS = 15_000 // 15 секунд (уменьшаем нагрузку на сервер)
const PAYMENT_TIMEOUT_MS = 15 * 60 * 1000 // 15 минут

/**
 * Composable для polling активных заказов
 * 
 * @description
 * Выполняет периодический опрос API для проверки статуса заказов.
 * 
 * ✅ ОБНОВЛЕНО: Polling работает ПОСТОЯННО с интервалом 15 секунд
 * Фильтрует только:
 * - Неоплаченные заказы старше 15 минут (они скрываются из списка)
 * 
 * Останавливается только когда:
 * - Пользователь вышел из системы
 * - Компонент размонтирован
 */
export function useUnpaidOrdersPolling() {
  // ============================================
  // STATE
  // ============================================
  
  /**
   * Интервал для polling
   */
  let pollingInterval: NodeJS.Timeout | null = null
  
  /**
   * Флаг активности polling
   */
  const isPolling = ref(false)
  
  /**
   * Флаг загрузки данных
   */
  const isLoading = ref(false)
  
  /**
   * Ошибка polling
   */
  const error = ref<string | null>(null)
  
  // ============================================
  // HELPERS
  // ============================================
  
  /**
   * Проверяет, истекло ли время оплаты для заказа
   */
  function isPaymentTimeExpired(createdAt: string): boolean {
    const now = Date.now()
    const orderTime = new Date(createdAt).getTime()
    const elapsed = now - orderTime
    
    return elapsed >= PAYMENT_TIMEOUT_MS
  }
  
  /**
   * Проверяет, нужно ли продолжать polling для заказа
   * 
   * ✅ ОБНОВЛЕНО: Polling продолжается ВСЕГДА (для всех статусов)
   * Фильтруем только истекшие неоплаченные заказы (старше 15 минут)
   */
  function shouldContinuePolling(order: Order | OrderSummary): boolean {
    // ✅ НОВОЕ: Для неоплаченных заказов проверяем истечение времени
    if (order.paymentStatus !== PAYMENT_STATUSES.PAID && isPaymentTimeExpired(order.createdAt)) {
        consola.debug('⏰ [Polling] Payment time expired for unpaid order:', order.id)
        return false
      }
    
    // ✅ Продолжаем polling для ВСЕХ остальных заказов:
    // - Неоплаченных заказов (pending, waiting) в пределах 15 минут
    // - Оплаченных заказов (paid) с ЛЮБЫМИ статусами (включая delivered/cancelled)
    consola.debug('🔄 [Polling] Continue polling for order:', order.id, {
      status: order.status,
      paymentStatus: order.paymentStatus,
    })
    
    return true
  }
  
  /**
   * Фильтрует заказы, для которых нужно продолжать polling
   */
  function filterActiveOrders(orders: OrderSummary[]): OrderSummary[] {
    return orders.filter(order => shouldContinuePolling(order))
  }
  
  // ============================================
  // POLLING LOGIC
  // ============================================
  
  /**
   * Запускает polling для неоплаченных заказов
   * 
   * @param fetchCallback - callback для загрузки заказов с API
   * @param onUpdate - callback вызывается при обновлении данных
   * @param onComplete - callback вызывается когда все заказы завершены
   */
  function startPolling(
    fetchCallback: () => Promise<void>,
    onUpdate?: (orders: OrderSummary[]) => void,
    onComplete?: () => void,
  ): void {
    consola.debug('🔄 [Polling] Starting polling for unpaid orders')
    
    // Останавливаем предыдущий polling если он был
    stopPolling()
    
    isPolling.value = true
    error.value = null
    
    // Первый запрос сразу
    executePoll(fetchCallback, onUpdate, onComplete)
    
    // Затем каждые POLLING_INTERVAL_MS
    pollingInterval = setInterval(() => {
      executePoll(fetchCallback, onUpdate, onComplete)
    }, POLLING_INTERVAL_MS)
    
    consola.debug('✅ [Polling] Polling started successfully')
  }
  
  /**
   * Выполняет один цикл polling
   */
  async function executePoll(
    fetchCallback: () => Promise<void>,
    onUpdate?: (orders: OrderSummary[]) => void,
    _onComplete?: () => void,
  ): Promise<void> {
    try {
      isLoading.value = true
      error.value = null
      
      consola.debug('🔄 [Polling] Fetching unpaid orders...')
      await fetchCallback()
      
      consola.debug('✅ [Polling] Orders fetched successfully')
      
      void onUpdate
      
    } catch (error_: any) {
      consola.error('❌ [Polling] Error during poll:', error_)
      error.value = error_.message || 'Failed to fetch orders'
      
      // Не останавливаем polling при ошибке, продолжаем попытки
    } finally {
      isLoading.value = false
    }
  }
  
  /**
   * Останавливает polling
   */
  function stopPolling(): void {
    consola.debug('🛑 [Polling] Stopping polling')
    
    if (pollingInterval) {
      clearInterval(pollingInterval)
      pollingInterval = null
    }
    
    isPolling.value = false
    consola.debug('✅ [Polling] Polling stopped')
  }
  
  /**
   * Проверяет, нужно ли останавливать polling на основе текущих заказов
   */
  function checkAndStopIfNeeded(orders: OrderSummary[], onComplete?: () => void): void {
    const activeOrders = filterActiveOrders(orders)
    
    consola.debug('🔍 [Polling] Checking orders:', {
      total: orders.length,
      active: activeOrders.length,
      shouldStop: activeOrders.length === 0,
    })
    
    // Если нет активных заказов - останавливаем polling
    if (activeOrders.length === 0 && orders.length > 0) {
      consola.debug('🏁 [Polling] No active orders remaining, stopping polling')
      stopPolling()
      
      if (onComplete) {
        onComplete()
      }
    }
  }
  
  /**
   * Перезапускает polling
   */
  function restartPolling(
    fetchCallback: () => Promise<void>,
    onUpdate?: (orders: OrderSummary[]) => void,
    onComplete?: () => void,
  ): void {
    consola.debug('🔄 [Polling] Restarting polling')
    stopPolling()
    startPolling(fetchCallback, onUpdate, onComplete)
  }
  
  // ============================================
  // LIFECYCLE
  // ============================================
  
  // Останавливаем polling при размонтировании
  onUnmounted(() => {
    consola.debug('🧹 [Polling] Component unmounted, stopping polling')
    stopPolling()
  })
  
  // ============================================
  // RETURN
  // ============================================
  
  return {
    // State
    isPolling: computed(() => isPolling.value),
    isLoading: computed(() => isLoading.value),
    error: computed(() => error.value),
    
    // Actions
    startPolling,
    stopPolling,
    restartPolling,
    checkAndStopIfNeeded,
    
    // Helpers
    shouldContinuePolling,
    filterActiveOrders,
    isPaymentTimeExpired,
  }
}

